import React, {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useRef,
} from 'react';
import c from 'classnames';

import Button from '../Button';

import './style.scss';

const ARROW_LEFT = 'ArrowLeft';
const ARROW_RIGHT = 'ArrowRight';
const ARROW_UP = 'ArrowUp';
const ARROW_DOWN = 'ArrowDown';

export interface SwitchTab {
  key: string;
  label: string;
  icon?: string;
}

export interface SwitchProps {
  tabs: SwitchTab[];
  currentTab: string;
  idPrefix?: string;
  tabPanelRef?: RefObject<HTMLDivElement>;
  className?: string;
  onChange?: (key: string) => void;
}

const Switch: React.FC<SwitchProps> = (props) => {
  const {
    tabs,
    currentTab,
    idPrefix = 'tab',
    tabPanelRef,
    className,
    onChange,
  } = props;

  const ref = useRef<HTMLDivElement>(null);

  const classNames = c('Switch', className);
  const tabKeys = tabs.map((tab) => tab.key);
  const currentTabIndex = tabKeys.findIndex((tabKey) => tabKey === currentTab);
  const tabPanelId = tabPanelRef?.current?.id;

  function toggleTab(tabToSelectKey: string) {
    if (currentTabIndex < 0 || ref.current == null) {
      return;
    }

    const tabToSelect = ref.current.querySelector(`[data-switch-tab-key="${tabToSelectKey}"]`);

    if (!tabToSelect) {
      return;
    }

    (tabToSelect as HTMLElement).focus();

    if (onChange) {
      onChange(tabToSelectKey);
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    const { key } = event;

    if (currentTabIndex < 0 || ref.current == null) {
      return;
    }

    if (![ARROW_LEFT, ARROW_UP, ARROW_RIGHT, ARROW_DOWN].includes(key)) {
      return;
    }

    event.preventDefault();

    // Previous tab: -1. Next tab: 1.
    const direction = key === ARROW_LEFT || key === ARROW_UP ? -1 : 1;
    const tabToSelectKey = tabKeys[currentTabIndex + direction];

    if (tabToSelectKey == null) {
      return;
    }

    toggleTab(tabToSelectKey);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    const tabToSelectKey = event.currentTarget.dataset.switchTabKey;

    if (tabToSelectKey == null) {
      return;
    }

    toggleTab(tabToSelectKey);
  };

  return (
    <div className={classNames} role="tablist" ref={ref}>
      {tabs.map((tab) => {
        const isCurrentTab = currentTab === tab.key;

        return (
          <Button
            id={`${idPrefix}-${tab.key}`}
            role="tab"
            aria-selected={isCurrentTab}
            tabIndex={isCurrentTab ? 0 : -1}
            aria-controls={tabPanelId}
            data-switch-tab-key={tab.key}
            type={isCurrentTab ? 'primary' : 'secondary'}
            icon={tab.icon}
            iconPosition="left"
            onKeyDown={handleKeyDown}
            onClick={handleClick}
          >
            {tab.label}
          </Button>
        );
      })}
    </div>
  );
};

export default Switch;
